import { CdkDialogContainer } from "@angular/cdk/dialog";
import { ChangeDetectionStrategy, Component, ComponentRef, EventEmitter, OnDestroy, ViewEncapsulation } from "@angular/core";
import { VdsDialogAppearanceType, VdsDialogConfig } from "../dialog-config";
import { CdkPortalOutlet, ComponentPortal } from "@angular/cdk/portal";

const OPENING_CLASS = "vds-dialog-container--enter";
const CLOSING_CLASS = "vds-dialog-container--leave";

/* Duration of the animations in milliseconds */
export const DIALOG_ANIMATION_DURATION = 400;

/** Event that captures the state of dialog container animations. */
interface DialogAnimationEvent {
    state: "opened" | "opening" | "closing" | "closed";
    totalTime: number;
}

@Component({
    selector: "vds-dialog-container",
    template: "<ng-template cdkPortalOutlet />",
    styleUrl: "../dialog.css",
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    host: {
        class: "vds-dialog-container",
        "[class]": "appearance ? 'vds-dialog-container--' + appearance : ''",
        tabindex: "-1",
        "[attr.aria-modal]": "_config.ariaModal",
        "[id]": "_config.id",
        "[attr.role]": "_config.role",
        "[attr.aria-labelledby]": "_config.ariaLabel ? null : _ariaLabelledByQueue[0]",
        "[attr.aria-label]": "_config.ariaLabel",
        "[attr.aria-describedby]": "_config.ariaDescribedBy || null"
    },
    imports: [CdkPortalOutlet]
})
export class VdsDialogContainer extends CdkDialogContainer<VdsDialogConfig> implements OnDestroy {
    private hostElement: HTMLElement = this._elementRef.nativeElement;

    /** Emits when an animation state changes. */
    animationStateChanged = new EventEmitter<DialogAnimationEvent>();

    /** Current timer for dialog animations. */
    private _animationTimer: ReturnType<typeof setTimeout> | null = null;

    get appearance(): VdsDialogAppearanceType {
        return this._config.appearance!;
    }

    protected override _contentAttached() {
        // Delegate to the original dialog-container initialization (i.e. saving the
        // previous element, setting up the focus trap and moving focus to the container).
        super._contentAttached();

        this._startOpenAnimation();
    }

    private _startOpenAnimation() {
        this.animationStateChanged.emit({ state: "opening", totalTime: DIALOG_ANIMATION_DURATION });

        this._requestAnimationFrame(() => this.hostElement.classList.add(OPENING_CLASS));
        this._waitForAnimationToComplete(DIALOG_ANIMATION_DURATION, this._finishDialogOpen);
    }

    _startExitAnimation(): void {
        this.animationStateChanged.emit({ state: "closing", totalTime: DIALOG_ANIMATION_DURATION });

        this._requestAnimationFrame(() => this.hostElement.classList.add(CLOSING_CLASS));
        this._waitForAnimationToComplete(DIALOG_ANIMATION_DURATION, this._finishDialogClose);
    }

    /**
     * Completes the dialog open by clearing potential animation classes, trapping
     * focus and emitting an opened event.
     */
    private _finishDialogOpen = () => {
        this._clearAnimationClasses();
        this._openAnimationDone(DIALOG_ANIMATION_DURATION);
    };

    /**
     * Completes the dialog close by clearing potential animation classes, restoring
     * focus and emitting a closed event.
     */
    private _finishDialogClose = () => {
        this._clearAnimationClasses();
        this.animationStateChanged.emit({ state: "closed", totalTime: DIALOG_ANIMATION_DURATION });
    };

    /** Clears all dialog animation classes. */
    private _clearAnimationClasses() {
        this.hostElement.classList.remove(OPENING_CLASS, CLOSING_CLASS);
    }

    private _waitForAnimationToComplete(duration: number, callback: () => void) {
        if (this._animationTimer !== null) {
            clearTimeout(this._animationTimer);
        }

        // Note that we want this timer to run inside the NgZone, because we want
        // the related events like `afterClosed` to be inside the zone as well.
        this._animationTimer = setTimeout(callback, duration);
    }

    /** Runs a callback in `requestAnimationFrame`, if available. */
    private _requestAnimationFrame(callback: () => void) {
        this._ngZone.runOutsideAngular(() => {
            if (typeof requestAnimationFrame === "function") {
                requestAnimationFrame(callback);
            } else {
                callback();
            }
        });
    }

    protected override _captureInitialFocus() {
        if (!this._config.delayFocusTrap) {
            this._trapFocus();
        }
    }

    /**
     * Callback for when the open dialog animation has finished. Intended to
     * be called by sub-classes that use different animation implementations.
     */
    protected _openAnimationDone(totalTime: number) {
        if (this._config.delayFocusTrap) {
            this._trapFocus();
        }

        this.animationStateChanged.next({ state: "opened", totalTime });
    }

    override attachComponentPortal<T>(portal: ComponentPortal<T>): ComponentRef<T> {
        /**
         * when a component is passed into the dialog, the host element interrupts
         * the `display: flex` from affecting the dialog header, content and actions.
         * To fix this, we make the component host `display: contents` by marking its host
         * with the `vds-dialog-component-host` class.
         *
         * Note that this problem does not affect using template ref since the header, contents and actions
         * are then nested directly under the dialog surface.
         */
        const ref = super.attachComponentPortal(portal);
        ref.location.nativeElement.classList.add("vds-dialog-component-host");
        return ref;
    }
}
